/**
 * O quadro dos pedidos em andamento, uma coluna por etapa.
 *
 * Papel: distribui os pedidos pelas cinco etapas de `STATUS_NO_QUADRO`, em
 * colunas ligadas por uma seta `→` no vão entre elas, cada pedido como um
 * cartão com número e casa, a descrição dos itens, o fornecedor e o total, a
 * data da solicitação, o solicitante e a categoria, e os botões de movimento.
 *
 * Como usar: quem monta o quadro passa **todos** os pedidos, e a separação
 * acontece aqui. O pedido `finalizado` não aparece: ele vai para a tabela de
 * finalizados, abaixo do quadro. `aprovado` e `reprovado` também não têm coluna
 * — são o instante de uma decisão —, e um pedido gravado num deles some do
 * quadro; é por isso que o formulário nunca os grava sem passar pela caixa de
 * decisão.
 *
 * O quadro tem largura mínima de 1000px e rola na horizontal dentro do próprio
 * contêiner, sem empurrar a página: as colunas ficam elásticas acima de 1300px,
 * fixas em 180px até 920px, e em 160px com o vão encolhido abaixo disso — a
 * seta acompanha o vão, e por isso muda de posição junto com ele.
 *
 * Os botões de movimento só aparecem para quem pode editar. Avançar de
 * `em_aprovacao` ou de `em_processo_de_compra` não move o cartão sozinho — as
 * duas etapas exigem uma decisão, e o quadro chama quem monta para pedi-la.
 * "✕ Reprovar" existe só no cartão em aprovação, que é a única etapa de onde um
 * pedido pode ser recusado.
 *
 * O total de cada cartão é `totalDoPedido`, do pacote compartilhado, a mesma
 * conta que a tabela de finalizados usa — somar aqui de outro jeito faria as
 * duas leituras do mesmo pedido discordarem.
 *
 * Data, solicitante e categoria deverão continuar no cartão, na linha abaixo
 * do fornecedor: o nível `ver` não abre o formulário, e um pedido em andamento
 * não tem outro lugar em `/compras` onde esses três apareçam para ele.
 *
 * A seleção para a ação em lote é de quem monta: com `selecionados` e
 * `onSelecionar`, cada cartão ganha a caixa no canto de cima à direita e cada
 * coluna com pedido ganha o "Todos", que marca ou desmarca os cartões só dela.
 * Sem `onSelecionar` as caixas somem, como os botões de movimento. A caixa fica
 * fora do botão do corpo do cartão, e por isso marcar não abre o pedido.
 */
import {
  type PedidoCompra,
  STATUS_NO_QUADRO,
  type StatusPedidoCompra,
  totalDoPedido,
} from '@concierge/shared';

import { formatarData, formatarDinheiro } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import { ROTULOS_CATEGORIA, ROTULOS_STATUS } from '../rotulos';
import { fornecedorDoPedido, residenciaDoPedido } from '../resumo';

interface QuadroComprasProps {
  pedidos: PedidoCompra[];
  /** Chamado com o pedido do cartão clicado; ausente deixa o cartão inerte. */
  onAbrir?: (pedido: PedidoCompra) => void;
  /** Chamado ao pedir avanço; ausente esconde os botões de movimento. */
  onAvancar?: (pedido: PedidoCompra) => void;
  /** Chamado ao pedir retrocesso; ausente esconde os botões de movimento. */
  onRetroceder?: (pedido: PedidoCompra) => void;
  /** Chamado ao pedir a reprovação de um pedido em aprovação. */
  onReprovar?: (pedido: PedidoCompra) => void;
  /** Os `id` dos pedidos marcados para a ação em lote. */
  selecionados?: ReadonlySet<string>;
  /**
   * Chamado com os pedidos cuja caixa mudou — um, pela caixa do cartão, ou os
   * da coluna, pelo "Todos" — e o estado novo; ausente esconde as caixas.
   */
  onSelecionar?: (pedidos: readonly PedidoCompra[], marcado: boolean) => void;
}

/**
 * A seta `→` desenhada no vão à direita de cada coluna que não é a última.
 *
 * Fica no `::after` da própria coluna, e não num elemento entre elas, porque a
 * grade não reserva trilha para a seta: ela vive no `gap`, e o deslocamento
 * `-right-[30px]` é exatamente a largura desse vão — por isso passa a
 * `-right-[22px]` na mesma largura em que o vão encolhe para 22px.
 */
const SETA_ENTRE_COLUNAS =
  "after:pointer-events-none after:absolute after:top-1/2 after:-right-[30px] after:flex after:size-[26px] after:-translate-y-1/2 after:items-center after:justify-center after:font-sans after:text-[18px] after:leading-none after:text-brass after:content-['→'] max-[921px]:after:-right-[22px]";

/**
 * O quadro.
 *
 * @param pedidos - Todos os pedidos; os que não estão numa das cinco etapas do
 *   quadro são descartados aqui.
 * @param onAbrir - Chamado ao clicar no corpo de um cartão; ausente deixa os
 *   cartões inertes, que é o que acontece no nível `ver`.
 * @param onAvancar - Chamado em "Avançar →".
 * @param onRetroceder - Chamado em "←".
 * @param onReprovar - Chamado em "✕ Reprovar", no cartão em aprovação.
 * @param selecionados - Os `id` marcados; o cartão marcado ganha a borda e o
 *   anel em latão, e o "Todos" fica marcado quando todos os da coluna estão.
 * @param onSelecionar - Chamado pela caixa do cartão e pelo "Todos" da coluna.
 * @returns As cinco colunas, cada uma com os seus cartões, dentro do contêiner
 *   que rola na horizontal.
 */
export function QuadroCompras({
  pedidos,
  onAbrir,
  onAvancar,
  onRetroceder,
  onReprovar,
  selecionados,
  onSelecionar,
}: QuadroComprasProps) {
  const porEtapa = new Map<StatusPedidoCompra, PedidoCompra[]>();
  for (const etapa of STATUS_NO_QUADRO) porEtapa.set(etapa, []);
  for (const pedido of pedidos) {
    const coluna = porEtapa.get(pedido.status);
    if (coluna !== undefined) coluna.push(pedido);
  }

  return (
    <div className="-mx-1 overflow-x-auto pb-3.5">
      <div className="grid min-w-[1000px] grid-cols-[repeat(5,minmax(180px,1fr))] gap-x-[30px] px-5 pt-1 pb-5 max-[1301px]:grid-cols-[repeat(5,180px)] max-[921px]:grid-cols-[repeat(5,160px)] max-[921px]:gap-x-[22px]">
        {STATUS_NO_QUADRO.map((etapa, indice) => {
          const daEtapa = porEtapa.get(etapa) ?? [];
          const ultima = indice === STATUS_NO_QUADRO.length - 1;
          const todosMarcados =
            daEtapa.length > 0 &&
            daEtapa.every((pedido) => selecionados?.has(pedido.id) === true);

          return (
            <section
              key={etapa}
              aria-label={ROTULOS_STATUS[etapa]}
              className={cn(
                'relative flex max-h-[70vh] min-w-0 flex-col gap-2.5 rounded-md border border-linha-forte bg-superficie-2 p-3',
                !ultima && SETA_ENTRE_COLUNAS,
              )}
            >
              <header className="flex items-center justify-between gap-2 border-b border-linha-forte pb-2.5 font-mono text-[10px] tracking-[0.04em] text-texto-fraco uppercase">
                <h3>{ROTULOS_STATUS[etapa]}</h3>
                <span className="flex items-center gap-2">
                  {onSelecionar !== undefined && daEtapa.length > 0 && (
                    <label
                      title="Selecionar todos desta coluna"
                      className="flex cursor-pointer items-center gap-1.5 print:hidden"
                    >
                      <input
                        type="checkbox"
                        className="cursor-pointer"
                        checked={todosMarcados}
                        onChange={(evento) => onSelecionar(daEtapa, evento.target.checked)}
                      />
                      <span>Todos</span>
                    </label>
                  )}
                  <span className="tabular">
                    <span className="sr-only">Pedidos nesta etapa: </span>
                    {daEtapa.length}
                  </span>
                </span>
              </header>

              {daEtapa.length === 0 ? (
                <p className="px-1 py-[18px] text-center text-[11.5px] text-texto-fraco">
                  Nenhum pedido
                </p>
              ) : (
                <ul className="flex list-none flex-col gap-2 overflow-y-auto p-0 pr-0.5">
                  {daEtapa.map((pedido) => (
                    <li key={pedido.id}>
                      <Cartao
                        pedido={pedido}
                        primeiraEtapa={indice === 0}
                        onAbrir={onAbrir}
                        onAvancar={onAvancar}
                        onRetroceder={onRetroceder}
                        onReprovar={onReprovar}
                        selecionado={selecionados?.has(pedido.id) === true}
                        onSelecionar={onSelecionar}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

interface CartaoProps {
  pedido: PedidoCompra;
  /** Se o cartão está na primeira coluna, de onde não há para onde voltar. */
  primeiraEtapa: boolean;
  onAbrir?: (pedido: PedidoCompra) => void;
  onAvancar?: (pedido: PedidoCompra) => void;
  onRetroceder?: (pedido: PedidoCompra) => void;
  onReprovar?: (pedido: PedidoCompra) => void;
  /** Se o pedido está marcado para a ação em lote. */
  selecionado: boolean;
  onSelecionar?: (pedidos: readonly PedidoCompra[], marcado: boolean) => void;
}

/** O desenho comum aos botões de movimento, menores e mais discretos que `Button`. */
const BOTAO_DE_MOVIMENTO =
  'flex-1 cursor-pointer rounded-[4px] border border-linha-forte bg-superficie px-2 py-1.5 font-sans text-[11px] text-texto-medio transition-[background-color,border-color,color] duration-150 ease-in-out enabled:hover:border-brass enabled:hover:bg-superficie-2 enabled:hover:text-brass-light disabled:cursor-not-allowed disabled:opacity-35';

/**
 * Um cartão do quadro.
 *
 * @param pedido - O pedido a desenhar.
 * @param primeiraEtapa - Desabilita "←", porque a primeira coluna não tem
 *   etapa anterior.
 * @param onAbrir - Chamado ao clicar no corpo do cartão; ausente deixa o cartão
 *   inerte, sem aparência de clicável.
 * @param onAvancar - Chamado em "Avançar →"; ausente, junto com
 *   `onRetroceder`, esconde a faixa de botões inteira.
 * @param onRetroceder - Chamado em "←".
 * @param onReprovar - Chamado em "✕ Reprovar", que só aparece em
 *   `em_aprovacao`.
 * @param selecionado - Desenha o cartão marcado: borda inteira e anel interno
 *   em latão, sobre o fundo `superficie-2`.
 * @param onSelecionar - Chamado pela caixa do canto, com o pedido sozinho;
 *   ausente esconde a caixa.
 * @returns O cartão, com o resumo do pedido e os botões de movimento.
 */
function Cartao({
  pedido,
  primeiraEtapa,
  onAbrir,
  onAvancar,
  onRetroceder,
  onReprovar,
  selecionado,
  onSelecionar,
}: CartaoProps) {
  const descricoes =
    pedido.itens.length === 0
      ? ['Sem descrição']
      : pedido.itens.map(
          (item) =>
            `${item.descricao === '' ? 'Sem descrição' : item.descricao}${item.quantidade > 1 ? ` (x${String(item.quantidade)})` : ''}`,
        );

  const conteudo = (
    <>
      <span className="mb-1 block font-mono text-[10px] text-texto-fraco">
        {pedido.numero === '' ? '—' : pedido.numero} · {residenciaDoPedido(pedido)}
      </span>
      <span
        className="mb-2 block text-[13px] leading-[1.4] text-foreground"
        title={pedido.itens.length === 0 ? undefined : descricoes.join(' | ')}
      >
        {descricoes.map((descricao, posicao) => (
          <span key={posicao} className={cn('block', posicao > 0 && 'mt-0.5')}>
            {descricao}
          </span>
        ))}
      </span>
      <span className="flex items-center justify-between gap-1.5 text-[11px] text-texto-medio">
        <span>{fornecedorDoPedido(pedido) ?? '—'}</span>
        <span className="font-mono text-brass-light">
          {formatarDinheiro(totalDoPedido(pedido.itens))}
        </span>
      </span>
      <span className="mt-1 block text-[11px] text-texto-medio">
        <span>{formatarData(pedido.data)}</span>
        {' · '}
        <span>{pedido.solicitante === '' ? '—' : pedido.solicitante}</span>
        {' · '}
        <span>{ROTULOS_CATEGORIA[pedido.categoria]}</span>
      </span>
      {pedido.motivoReprovacao !== null && (
        <span title={pedido.motivoReprovacao} className="font-mono text-[10px] text-perigo">
          ✕ Reprovado
        </span>
      )}
    </>
  );

  return (
    <div
      className={cn(
        'relative rounded-[4px] border border-l-[3px] border-linha-forte border-l-brass bg-superficie px-3 py-2.5',
        onAbrir !== undefined &&
          'transition-[border-color,transform,box-shadow] duration-150 ease-in-out hover:-translate-y-px hover:border-brass hover:shadow-cartao',
        selecionado && 'border-brass bg-superficie-2 inset-ring-2 inset-ring-brass',
      )}
    >
      {onSelecionar !== undefined && (
        <label
          title="Selecionar para ação em lote"
          className="absolute top-2 right-2 z-[1] cursor-pointer leading-none print:hidden"
        >
          <input
            type="checkbox"
            aria-label={`Selecionar ${pedido.numero === '' ? 'pedido sem número' : pedido.numero}`}
            className="size-4 cursor-pointer accent-brass"
            checked={selecionado}
            onChange={(evento) => onSelecionar([pedido], evento.target.checked)}
          />
        </label>
      )}
      {onAbrir === undefined ? (
        <div>{conteudo}</div>
      ) : (
        <button
          type="button"
          title="Abrir pedido"
          onClick={() => onAbrir(pedido)}
          className="block w-full cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass-light"
        >
          {conteudo}
        </button>
      )}

      {onAvancar !== undefined && onRetroceder !== undefined && (
        <div className="mt-2.5 flex gap-1.5 border-t border-linha-forte pt-2 print:hidden">
          <button
            type="button"
            title="Voltar etapa"
            aria-label="Voltar etapa"
            className={BOTAO_DE_MOVIMENTO}
            disabled={primeiraEtapa}
            onClick={() => onRetroceder(pedido)}
          >
            ←
          </button>
          {pedido.status === 'em_aprovacao' && onReprovar !== undefined && (
            <button
              type="button"
              title="Reprovar pedido"
              className={cn(BOTAO_DE_MOVIMENTO, 'text-perigo enabled:hover:text-perigo')}
              onClick={() => onReprovar(pedido)}
            >
              ✕ Reprovar
            </button>
          )}
          <button
            type="button"
            title="Avançar etapa"
            className={BOTAO_DE_MOVIMENTO}
            onClick={() => onAvancar(pedido)}
          >
            Avançar →
          </button>
        </div>
      )}
    </div>
  );
}
